import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { ResumoPA, PrimeirosAtendimentosDialog } from '../components/primeiros/PrimeirosAtendimentos';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { MapPicker } from '../components/ui/map-picker';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle2,
  UserPlus,
  Stethoscope,
  UserRound,
  RefreshCw,
  LayoutGrid,
  Plus,
  Sparkles,
  Loader2,
  Users,
  Route,
  MapPin,
  Navigation,
  Settings,
  Pencil,
  Trash2,
  Palette,
  Share2,
  Printer,
  Mail,
  MessageCircle,
  Download,
  Map,
  Search,
  X,
  Lock,
  Unlock
} from 'lucide-react';
import { formatDate, getDaysAlertLevel, STATUS_LABELS, ORIGEM_LABELS, ORIGEM_COLORS, TASK_TYPE_LABELS, PRIORITY_LABELS, todayLocal, displayCPF } from '../lib/utils';
import { toast } from 'sonner';

// Origem columns - Cardiologia, Paliativo, Clínico, Cirurgia, PACS, HMI, Outro
// Origem v2: 3 main groups
const ORIGEM_GRUPO_COLUMNS = [
  { id: 'HGR', title: 'HGR', color: 'border-t-blue-500', bgLight: 'bg-blue-50 dark:bg-blue-900/20' },
  { id: 'HMI', title: 'HMI', color: 'border-t-amber-500', bgLight: 'bg-amber-50 dark:bg-amber-900/20' },
  { id: 'PACS', title: 'PACS', color: 'border-t-cyan-500', bgLight: 'bg-cyan-50 dark:bg-cyan-900/20' },
];

// HGR subgroup columns (expanded view)
const HGR_SUBGRUPO_COLUMNS = [
  { id: 'completar_atb', title: 'Completar ATB', color: 'border-t-teal-500', bgLight: 'bg-teal-50 dark:bg-teal-900/20' },
  { id: 'cardiologia', title: 'Cardiologia', color: 'border-t-red-500', bgLight: 'bg-red-50 dark:bg-red-900/20' },
  { id: 'paliativo', title: 'Paliativo', color: 'border-t-purple-500', bgLight: 'bg-purple-50 dark:bg-purple-900/20' },
  { id: 'clinico', title: 'Clínico', color: 'border-t-blue-500', bgLight: 'bg-blue-50 dark:bg-blue-900/20' },
  { id: 'cirurgia', title: 'Cirurgia', color: 'border-t-orange-500', bgLight: 'bg-orange-50 dark:bg-orange-900/20' },
];

// LOS (Length of Stay) columns
const LOS_COLUMNS = [
  { id: '0-5', title: '0-5 dias', color: 'border-t-green-500', bgLight: 'bg-green-50 dark:bg-green-900/20' },
  { id: '6-15', title: '6-15 dias', color: 'border-t-blue-500', bgLight: 'bg-blue-50 dark:bg-blue-900/20' },
  { id: '16-30', title: '16-30 dias', color: 'border-t-orange-500', bgLight: 'bg-orange-50 dark:bg-orange-900/20' },
  { id: '>30', title: '> 30 dias', color: 'border-t-red-500', bgLight: 'bg-red-50 dark:bg-red-900/20' },
];

const Kanban = () => {
  const { api, canViewFullCPF, can } = useAuth();
  const readOnly = !can('mover_kanban');
  const canManageEquipes = can('gerenciar_equipes');
  const canCreateTask = can('criar_tarefas');
  const canOpenInternacao = can('ver_internacoes', 'ver_pacientes');
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('equipe'); // 'equipe' | 'origem' | 'los' | 'medico'
  const [paResumo, setPaResumo] = useState({});
  const [paSel, setPaSel] = useState(null);
  const loadPaResumo = useCallback(() => { api.get('/primeiros-atendimentos/resumo-kanban').then((r) => setPaResumo(r.data || {})).catch(() => {}); }, [api]);
  useEffect(() => { loadPaResumo(); }, [loadPaResumo]);
  const [kanbanSearch, setKanbanSearch] = useState('');
  const [especialidadeFilter, setEspecialidadeFilter] = useState('');
  const [hgrExpanded, setHgrExpanded] = useState(false);
  const [medicosData, setMedicosData] = useState([]);
  
  // Format CPF to XXX.XXX.XXX-XX
  const formatCPF = (cpf) => {
    if (!cpf) return '';
    const clean = cpf.replace(/\D/g, '');
    if (clean.length !== 11) return cpf;
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };
  
  // Equipe data
  const [equipeColumns, setEquipeColumns] = useState([]);
  const [equipeData, setEquipeData] = useState({});
  const [capacidade, setCapacidade] = useState(7);
  const [distribuindo, setDistribuindo] = useState(false);
  
  // Quick task modal state
  const [quickTaskModalOpen, setQuickTaskModalOpen] = useState(false);
  const [selectedInternacao, setSelectedInternacao] = useState(null);
  const [novaTarefa, setNovaTarefa] = useState({
    titulo: '',
    tipo: 'visita',
    data_prevista: todayLocal(),
    prioridade: 'media',
    status: 'a_fazer',
    observacoes: ''
  });

  // Itinerary modal state
  const [itinerarioModal, setItinerarioModal] = useState(false);
  const [itinerarioData, setItinerarioData] = useState(null);
  const [loadingItinerario, setLoadingItinerario] = useState(false);

  // Lock/Fix patient dialog state
  const [fixarDialogOpen, setFixarDialogOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);

  // Equipe editing state
  const [equipeModalOpen, setEquipeModalOpen] = useState(false);
  const [editingEquipe, setEditingEquipe] = useState(null);
  const [equipeForm, setEquipeForm] = useState({ 
    nome: '', 
    capacidade: 7, 
    cor: '#3B82F6',
    ponto_inicial_tipo: 'BASE',
    ponto_inicial_endereco_texto: '',
    ponto_inicial_lat: null,
    ponto_inicial_lng: null,
    ponto_inicial_descricao: '',
    ponto_inicial_tecnico_id: ''
  });
  const [savingEquipe, setSavingEquipe] = useState(false);
  const [manageEquipesModalOpen, setManageEquipesModalOpen] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  
  // Color options
  const colorOptions = [
    { value: '#3B82F6', name: 'Azul' },
    { value: '#10B981', name: 'Verde' },
    { value: '#F59E0B', name: 'Amarelo' },
    { value: '#EF4444', name: 'Vermelho' },
    { value: '#8B5CF6', name: 'Roxo' },
    { value: '#EC4899', name: 'Rosa' },
    { value: '#14B8A6', name: 'Teal' },
    { value: '#F97316', name: 'Laranja' },
  ];

  // Professional kanban data (when specialty filter is active)
  const [profColumns, setProfColumns] = useState([]);
  const [profData, setProfData] = useState({});

  useEffect(() => {
    if (especialidadeFilter) {
      fetchProfKanban();
    } else if (viewMode === 'equipe') {
      fetchEquipeKanban();
    } else {
      fetchKanbanData();
    }
    // Always fetch medicos for the medico view
    fetchMedicos();
  }, [viewMode, especialidadeFilter]);

  const fetchMedicos = async () => {
    try {
      const response = await api.get('/medicos?ativo=true');
      setMedicosData(response.data || []);
    } catch (error) {
      console.error('Error fetching medicos:', error);
    }
  };

  const fetchEquipeKanban = async () => {
    try {
      setLoading(true);
      const params = especialidadeFilter ? `?especialidade=${especialidadeFilter}` : '';
      const response = await api.get(`/equipes/kanban${params}`);
      setEquipeColumns(response.data.columns || []);
      setEquipeData(response.data.internacoes || {});
      setCapacidade(response.data.capacidade || 7);
      
      // Also set rawData for other views
      const allItems = [];
      Object.values(response.data.internacoes || {}).forEach(items => {
        allItems.push(...items);
      });
      setRawData(allItems);
    } catch (error) {
      console.error('Error fetching equipe kanban:', error);
      toast.error('Erro ao carregar Kanban por equipe');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfKanban = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/fisioterapia/kanban-profissionais?especialidade=${especialidadeFilter}`);
      const cols = (res.data.columns || []).map((c, idx) => ({
        id: c.id,
        title: c.title,
        cor: c.cor || '#3B82F6',
        capacidade: 0,
        tipo: 'profissional',
        color: `border-t-[${c.cor}]`,
        bgLight: '',
      }));
      setProfColumns(cols);
      setProfData(res.data.internacoes || {});
      
      // Also set rawData
      const allItems = [];
      Object.values(res.data.internacoes || {}).forEach(items => {
        allItems.push(...items);
      });
      setRawData(allItems);
    } catch (error) {
      console.error('Error fetching professional kanban:', error);
      toast.error('Erro ao carregar Kanban por profissional');
    } finally {
      setLoading(false);
    }
  };

  const fetchKanbanData = async () => {
    try {
      setLoading(true);
      const params = especialidadeFilter ? `?especialidade=${especialidadeFilter}` : '';
      const response = await api.get(`/kanban${params}`);
      const allItems = [];
      Object.values(response.data).forEach(items => {
        allItems.push(...items);
      });
      setRawData(allItems);
    } catch (error) {
      console.error('Error fetching kanban data:', error);
      toast.error('Erro ao carregar dados do Kanban');
    } finally {
      setLoading(false);
    }
  };

  const handleDistribuirIA = async () => {
    setDistribuindo(true);
    try {
      const response = await api.post('/equipes/distribuir-ia');
      toast.success(response.data.message);
      
      if (response.data.sem_coordenadas > 0) {
        toast.warning(`${response.data.sem_coordenadas} paciente(s) sem coordenadas foram deixados sem equipe`);
      }
      
      // Refresh kanban
      fetchEquipeKanban();
    } catch (error) {
      console.error('Error distributing:', error);
      toast.error('Erro ao distribuir equipes');
    } finally {
      setDistribuindo(false);
    }
  };

  // Fetch itinerary for a team
  const fetchItinerario = async (equipeId) => {
    setLoadingItinerario(true);
    try {
      const response = await api.get(`/equipes/${equipeId}/itinerario`);
      setItinerarioData(response.data);
      setItinerarioModal(true);
    } catch (error) {
      console.error('Error fetching itinerary:', error);
      toast.error('Erro ao carregar itinerário');
    } finally {
      setLoadingItinerario(false);
    }
  };

  // Fetch itinerary for a professional (specialty mode)
  const fetchItinerarioProfissional = async (profissionalId) => {
    setLoadingItinerario(true);
    try {
      const response = await api.get(`/fisioterapia/itinerario-profissional/${profissionalId}?especialidade=${especialidadeFilter}`);
      setItinerarioData(response.data);
      setItinerarioModal(true);
    } catch (error) {
      console.error('Error fetching professional itinerary:', error);
      toast.error('Erro ao carregar itinerário');
    } finally {
      setLoadingItinerario(false);
    }
  };

  // Fetch usuarios for tecnico selection
  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/usuarios/resumo');
      setUsuarios(response.data || []);
    } catch (error) {
      console.error('Error fetching usuarios:', error);
    }
  };

  // Equipe management functions
  const handleOpenEquipeModal = (equipe) => {
    setEditingEquipe(equipe);
    setEquipeForm({
      nome: equipe.title || equipe.nome,
      capacidade: equipe.capacidade || 7,
      cor: equipe.cor || '#3B82F6',
      ponto_inicial_tipo: equipe.ponto_inicial_tipo || 'BASE',
      ponto_inicial_endereco_texto: equipe.ponto_inicial_endereco_texto || '',
      ponto_inicial_lat: equipe.ponto_inicial_lat || null,
      ponto_inicial_lng: equipe.ponto_inicial_lng || null,
      ponto_inicial_descricao: equipe.ponto_inicial_descricao || '',
      ponto_inicial_tecnico_id: equipe.ponto_inicial_tecnico_id || ''
    });
    fetchUsuarios();
    setEquipeModalOpen(true);
  };

  const handleSaveEquipe = async () => {
    if (!equipeForm.nome.trim()) {
      toast.error('Digite o nome da equipe');
      return;
    }

    setSavingEquipe(true);
    try {
      await api.put(`/equipes/${editingEquipe.id}`, equipeForm);
      toast.success('Equipe atualizada com sucesso!');
      setEquipeModalOpen(false);
      fetchEquipeKanban();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar equipe');
    } finally {
      setSavingEquipe(false);
    }
  };

  const handleAddEquipe = async () => {
    // Fix: Use equipeColumns (not equipeData.columns) to get the current team count
    const currentTeamCount = equipeColumns?.length || 0;
    const newName = `Equipe ${currentTeamCount + 1}`;
    const colorIndex = currentTeamCount % colorOptions.length;
    try {
      await api.post('/equipes', { 
        nome: newName, 
        capacidade: 7, 
        cor: colorOptions[colorIndex].value 
      });
      toast.success('Equipe criada com sucesso!');
      fetchEquipeKanban();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('duplica') || detail.includes('existe')) {
        toast.error('Já existe uma equipe com esse nome.');
      } else if (err.response?.status === 403) {
        toast.error('Sem permissão para criar equipes.');
      } else {
        toast.error(detail || 'Erro ao criar equipe. Tente novamente.');
      }
    }
  };

  const handleDeleteEquipe = async (equipeId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta equipe?')) return;
    try {
      await api.delete(`/equipes/${equipeId}`);
      toast.success('Equipe excluída!');
      fetchEquipeKanban();
    } catch (err) {
      toast.error('Erro ao excluir equipe');
    }
  };

  // Generate itinerary text for export
  const generateItinerarioText = () => {
    if (!itinerarioData) return '';
    
    const hoje = new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' });
    const pontoInicial = itinerarioData.ponto_inicial;
    const pontoFinal = itinerarioData.ponto_final;
    
    let text = `🗺️ ITINERÁRIO - ${itinerarioData.equipe_nome}\n`;
    text += `📅 Data: ${hoje}\n`;
    
    // Add route starting/ending point header
    if (pontoInicial?.endereco) {
      text += `🚩 Início da rota: ${pontoInicial.endereco}\n`;
    }
    if (pontoFinal?.endereco) {
      text += `🏁 Fim da rota: ${pontoFinal.endereco}\n`;
    }
    
    text += `\n📊 RESUMO:\n`;
    text += `• ${itinerarioData.total_paradas} visitas\n`;
    text += `• ${itinerarioData.total_km} km total\n`;
    text += `• ~${itinerarioData.tempo_estimado_min} min estimado\n\n`;
    text += `📍 ROTEIRO:\n`;
    text += `${'─'.repeat(30)}\n`;
    
    itinerarioData.paradas?.forEach((parada, index) => {
      if (parada.tipo === 'origem') {
        text += `🏠 PARTIDA: ${parada.nome}\n`;
        text += `   ${parada.endereco || ''}\n\n`;
      } else if (parada.tipo === 'retorno') {
        text += `🏁 RETORNO: ${parada.nome}\n`;
        text += `   ${parada.endereco || ''}\n`;
        if (parada.distancia_anterior_km) {
          text += `   ↳ ${parada.distancia_anterior_km} km | ~${parada.tempo_anterior_min || Math.round(parada.distancia_anterior_km / 30 * 60)} min\n`;
        }
      } else {
        text += `${parada.ordem}. ${parada.nome}\n`;
        text += `   📍 ${parada.endereco || 'Endereço não disponível'}\n`;
        // Incluir telefone/contato
        if (parada.contato) {
          const contatoLabel = parada.contato_nome ? `📞 ${parada.contato_nome}: ${parada.contato}` : `📞 ${parada.contato}`;
          text += `   ${contatoLabel}\n`;
        } else {
          text += `   📞 —\n`;
        }
        if (parada.distancia_anterior_km) {
          text += `   ↳ ${parada.distancia_anterior_km} km | ~${parada.tempo_anterior_min || Math.round(parada.distancia_anterior_km / 30 * 60)} min\n`;
        }
        text += `\n`;
      }
    });
    
    return text;
  };

  // Export functions
  const handleExportWhatsApp = () => {
    const text = generateItinerarioText();
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  const handleExportEmail = () => {
    const text = generateItinerarioText();
    const subject = encodeURIComponent(`Itinerário - ${itinerarioData?.equipe_nome} - ${new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' })}`);
    const body = encodeURIComponent(text);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    toast.success('Abrindo cliente de e-mail...');
  };

  // Escape HTML to prevent XSS in print window
  const escapeHtml = (str) => {
    if (str == null) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  const handlePrint = () => {
    const text = generateItinerarioText();
    const hoje = new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' });
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Popup bloqueado. Permita popups para imprimir.'); return; }
    const html = `
      <html>
        <head>
          <title>Itinerário - ${escapeHtml(itinerarioData?.equipe_nome)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { 
              size: A4; 
              margin: 15mm 10mm 20mm 10mm;
            }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              padding: 0;
              line-height: 1.5;
              color: #333;
              font-size: 11pt;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 15px;
              border-bottom: 3px solid #DC2626;
              margin-bottom: 20px;
            }
            .logo-container {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .logo {
              height: 45px;
              width: auto;
            }
            .brand-name {
              font-size: 24pt;
              font-weight: bold;
              color: #DC2626;
            }
            .header-info {
              text-align: right;
              color: #666;
              font-size: 10pt;
            }
            .title {
              font-size: 16pt;
              font-weight: bold;
              color: #1a1a1a;
              margin-bottom: 15px;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .summary {
              display: flex;
              gap: 20px;
              background: #f8f9fa;
              padding: 12px 15px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .summary-item {
              text-align: center;
            }
            .summary-value {
              font-size: 18pt;
              font-weight: bold;
              color: #DC2626;
            }
            .summary-label {
              font-size: 9pt;
              color: #666;
            }
            .route-list {
              margin-top: 15px;
            }
            .route-item {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              padding: 10px 0;
              border-bottom: 1px dashed #ddd;
            }
            .route-item:last-child {
              border-bottom: none;
            }
            .route-number {
              width: 28px;
              height: 28px;
              border-radius: 50%;
              background: #DC2626;
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 10pt;
              flex-shrink: 0;
            }
            .route-number.origin {
              background: #16a34a;
            }
            .route-number.return {
              background: #dc2626;
            }
            .route-details {
              flex: 1;
            }
            .route-name {
              font-weight: 600;
              font-size: 11pt;
            }
            .route-address {
              color: #666;
              font-size: 9pt;
              margin-top: 2px;
            }
            .route-distance {
              color: #888;
              font-size: 8pt;
              margin-top: 2px;
              display: flex;
              gap: 10px;
            }
            .route-time {
              color: #DC2626;
              font-weight: 500;
            }
            .footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              padding: 10px 10mm;
              border-top: 2px solid #DC2626;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 8pt;
              color: #666;
              background: white;
            }
            .footer-logo {
              display: flex;
              align-items: center;
              gap: 5px;
            }
            .footer-logo img {
              height: 20px;
            }
            .footer-brand {
              font-weight: bold;
              color: #DC2626;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo-container">
              <img src="/assets/logo-symbol.png" alt="LifePort" class="logo" />
              <span class="brand-name">LifePort</span>
            </div>
            <div class="header-info">
              <div><strong>Itinerário de Visitas</strong></div>
              <div>${hoje}</div>
            </div>
          </div>
          
          <div class="title">
            🗺️ ${escapeHtml(itinerarioData?.equipe_nome)}
          </div>
          
          ${itinerarioData?.ponto_inicial?.endereco ? `
          <div style="background: #fef3c7; border: 1px solid #f59e0b; padding: 10px 15px; border-radius: 8px; margin-bottom: 15px;">
            <strong style="color: #92400e;">🚩 Início da rota:</strong> ${escapeHtml(itinerarioData.ponto_inicial.endereco)}
          </div>
          ` : ''}
          
          <div class="summary">
            <div class="summary-item">
              <div class="summary-value">${itinerarioData?.total_paradas || 0}</div>
              <div class="summary-label">Visitas</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${itinerarioData?.total_km || 0} km</div>
              <div class="summary-label">Distância Total</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">~${itinerarioData?.tempo_estimado_min || 0} min</div>
              <div class="summary-label">Tempo Estimado</div>
            </div>
          </div>
          
          <div class="route-list">
            ${itinerarioData?.paradas?.map((parada, index) => `
              <div class="route-item">
                <div class="route-number ${parada.tipo === 'origem' ? 'origin' : parada.tipo === 'retorno' ? 'return' : ''}">
                  ${parada.tipo === 'origem' ? '🏠' : parada.tipo === 'retorno' ? '🏁' : parada.ordem}
                </div>
                <div class="route-details">
                  <div class="route-name">
                    ${parada.tipo === 'origem' ? 'PARTIDA: ' : parada.tipo === 'retorno' ? 'RETORNO: ' : ''}
                    ${escapeHtml(parada.nome)}
                  </div>
                  <div class="route-address">📍 ${escapeHtml(parada.endereco || 'Endereço não disponível')}</div>
                  ${parada.tipo === 'visita' ? `
                    <div class="route-contact">📞 ${parada.contato ? (parada.contato_nome ? `${escapeHtml(parada.contato_nome)}: ${escapeHtml(parada.contato)}` : escapeHtml(parada.contato)) : '—'}</div>
                  ` : ''}
                  ${parada.distancia_anterior_km ? `
                    <div class="route-distance">
                      <span>↳ ${parada.distancia_anterior_km} km</span>
                      <span class="route-time">⏱️ ~${parada.tempo_anterior_min || Math.round(parada.distancia_anterior_km / 30 * 60)} min</span>
                    </div>
                  ` : ''}
                </div>
              </div>
            `).join('') || ''}
          </div>
          
          <div class="footer">
            <div class="footer-logo">
              <img src="/assets/logo-symbol.png" alt="LifePort" />
              <span class="footer-brand">LifePort</span>
              <span>- Sistema de Gestão de Home Care</span>
            </div>
            <div>Impresso em ${hoje} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleDownloadTxt = () => {
    const text = generateItinerarioText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `itinerario-${itinerarioData?.equipe_nome?.replace(/\s+/g, '-')}-${todayLocal()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Arquivo baixado!');
  };

  // Get LOS bucket for a patient
  const getLOSBucket = (dias) => {
    if (dias <= 5) return '0-5';
    if (dias <= 15) return '6-15';
    if (dias <= 30) return '16-30';
    return '>30';
  };

  // Get origem grupo key for Kanban grouping
  const getOrigemGrupoKey = (item) => {
    if (item.origem_grupo) return item.origem_grupo;
    // Fallback for legacy data
    const origem = item.origem;
    if (!origem) return 'HGR';
    const hgrSubgrupos = ['cardiologia', 'paliativo', 'clinico', 'cirurgia', 'completar_atb', 'hospital', 'outro'];
    const normalized = origem.toLowerCase().replace(/\s+/g, '_');
    if (normalized === 'pacs') return 'PACS';
    if (normalized === 'hmi') return 'HMI';
    return 'HGR';
  };

  const getOrigemSubgrupoKey = (item) => {
    if (item.origem_subgrupo) return item.origem_subgrupo;
    // Fallback for legacy data
    const origem = item.origem;
    if (!origem) return 'clinico';
    const normalized = origem.toLowerCase().replace(/\s+/g, '_');
    const validSubs = ['cardiologia', 'paliativo', 'clinico', 'cirurgia', 'completar_atb'];
    return validSubs.includes(normalized) ? normalized : 'clinico';
  };

  // Group data by current view mode
  const groupedData = useMemo(() => {
    // When specialty filter is active, use professional grouping
    if (especialidadeFilter && Object.keys(profData).length > 0) {
      return profData;
    }
    if (viewMode === 'equipe') {
      return equipeData;
    }
    
    const grouped = {};
    
    if (viewMode === 'origem') {
      if (hgrExpanded) {
        // Expanded: show HGR subgroups + HMI + PACS
        HGR_SUBGRUPO_COLUMNS.forEach(col => { grouped[`hgr_${col.id}`] = []; });
        grouped['HMI'] = [];
        grouped['PACS'] = [];
        rawData.forEach(item => {
          const grupo = getOrigemGrupoKey(item);
          if (grupo === 'HGR') {
            const sub = getOrigemSubgrupoKey(item);
            const key = `hgr_${sub}`;
            if (grouped[key]) grouped[key].push(item);
            else if (grouped['hgr_clinico']) grouped['hgr_clinico'].push(item);
          } else if (grouped[grupo] !== undefined) {
            grouped[grupo].push(item);
          }
        });
      } else {
        // Collapsed: 3 main groups
        ORIGEM_GRUPO_COLUMNS.forEach(col => { grouped[col.id] = []; });
        rawData.forEach(item => {
          const key = getOrigemGrupoKey(item);
          if (grouped[key]) grouped[key].push(item);
          else grouped['HGR']?.push(item);
        });
      }
    } else if (viewMode === 'los') {
      LOS_COLUMNS.forEach(col => { grouped[col.id] = []; });
      rawData.forEach(item => {
        const key = getLOSBucket(item.dias_internacao || 0);
        if (grouped[key]) {
          grouped[key].push(item);
        }
      });
    } else if (viewMode === 'medico') {
      grouped['sem_medico'] = [];
      medicosData.forEach(m => { grouped[m.id] = []; });
      rawData.forEach(item => {
        const medicoId = item.medico_id || item.medico_responsavel_id;
        if (medicoId && grouped[medicoId] !== undefined) {
          grouped[medicoId].push(item);
        } else {
          grouped['sem_medico'].push(item);
        }
      });
    }
    
    return grouped;
  }, [rawData, viewMode, equipeData, hgrExpanded, medicosData, especialidadeFilter, profData]);

  // Build medico columns dynamically
  const medicoColumns = useMemo(() => {
    const colors = ['border-t-blue-500', 'border-t-green-500', 'border-t-purple-500', 'border-t-amber-500', 'border-t-rose-500', 'border-t-cyan-500', 'border-t-orange-500', 'border-t-teal-500'];
    const bgColors = ['bg-blue-50 dark:bg-blue-900/20', 'bg-green-50 dark:bg-green-900/20', 'bg-purple-50 dark:bg-purple-900/20', 'bg-amber-50 dark:bg-amber-900/20', 'bg-rose-50 dark:bg-rose-900/20', 'bg-cyan-50 dark:bg-cyan-900/20', 'bg-orange-50 dark:bg-orange-900/20', 'bg-teal-50 dark:bg-teal-900/20'];
    const cols = medicosData.map((m, idx) => ({
      id: m.id,
      title: m.nome,
      color: colors[idx % colors.length],
      bgLight: bgColors[idx % bgColors.length],
    }));
    cols.push({ id: 'sem_medico', title: 'Sem médico', color: 'border-t-gray-400', bgLight: 'bg-gray-50 dark:bg-gray-900/20' });
    return cols;
  }, [medicosData]);

  // Get current columns based on view mode
  const currentColumns = useMemo(() => {
    // When specialty filter is active, show professional columns
    if (especialidadeFilter && profColumns.length > 0) {
      return profColumns;
    }
    switch (viewMode) {
      case 'equipe': return equipeColumns;
      case 'origem':
        if (hgrExpanded) {
          return [
            ...HGR_SUBGRUPO_COLUMNS.map(c => ({ ...c, id: `hgr_${c.id}`, title: `HGR — ${c.title}` })),
            { id: 'HMI', title: 'HMI', color: 'border-t-amber-500', bgLight: 'bg-amber-50 dark:bg-amber-900/20' },
            { id: 'PACS', title: 'PACS', color: 'border-t-cyan-500', bgLight: 'bg-cyan-50 dark:bg-cyan-900/20' },
          ];
        }
        return ORIGEM_GRUPO_COLUMNS;
      case 'los': return LOS_COLUMNS;
      case 'medico': return medicoColumns;
      default: return equipeColumns;
    }
  }, [viewMode, equipeColumns, hgrExpanded, medicoColumns, especialidadeFilter, profColumns]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (viewMode !== 'equipe' && viewMode !== 'medico' && !especialidadeFilter) {
      toast.info('Arraste apenas nos modos Equipe, Médico ou Especialidade');
      return;
    }

    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) return;

    // Specialty/professional mode
    if (especialidadeFilter) {
      executeMovePatientProfissional(source, destination, draggableId);
      return;
    }

    if (viewMode === 'medico') {
      // Handle medico drag-and-drop
      executeMovePatientMedico(source, destination, draggableId);
      return;
    }

    // Equipe mode - existing logic
    // Check if patient is locked
    const sourceItems = equipeData[source.droppableId] || [];
    const movedItem = sourceItems.find(i => i.id === draggableId);
    
    // Store pending move and show dialog
    setPendingMove({ 
      source, destination, draggableId, 
      pacienteNome: movedItem?.paciente_nome,
      isLocked: !!movedItem?.equipe_fixada
    });
    setFixarDialogOpen(true);
  };

  const executeMovePatientMedico = async (source, destination, draggableId) => {
    // Optimistic update in grouped data
    const newGrouped = { ...groupedData };
    const sourceItems = [...(newGrouped[source.droppableId] || [])];
    const destItems = [...(newGrouped[destination.droppableId] || [])];
    
    const itemIndex = sourceItems.findIndex(i => i.id === draggableId);
    if (itemIndex !== -1) {
      const [movedItem] = sourceItems.splice(itemIndex, 1);
      const newMedicoId = destination.droppableId === 'sem_medico' ? null : destination.droppableId;
      movedItem.medico_id = newMedicoId;
      destItems.push(movedItem);
      
      // Update rawData too
      setRawData(prev => prev.map(item => 
        item.id === draggableId ? { ...item, medico_id: newMedicoId } : item
      ));
    }

    try {
      await api.put(`/internacoes/${draggableId}/medico?medico_id=${destination.droppableId}`);
      const destColumn = medicoColumns.find(c => c.id === destination.droppableId);
      toast.success('Médico(a) atualizado com sucesso.');
    } catch (error) {
      console.error('Error updating medico:', error);
      toast.error('Erro ao atualizar médico');
      // Revert - refetch data
      fetchKanbanData();
    }
  };

  const executeMovePatientProfissional = async (source, destination, draggableId) => {
    // Optimistic update
    const newProfData = { ...profData };
    const sourceItems = [...(newProfData[source.droppableId] || [])];
    const destItems = [...(newProfData[destination.droppableId] || [])];

    const itemIndex = sourceItems.findIndex(i => i.id === draggableId);
    if (itemIndex !== -1) {
      const [movedItem] = sourceItems.splice(itemIndex, 1);
      const newProfId = destination.droppableId === 'sem_profissional' ? null : destination.droppableId;
      movedItem.profissional_id = newProfId;
      destItems.push(movedItem);

      newProfData[source.droppableId] = sourceItems;
      newProfData[destination.droppableId] = destItems;
      setProfData(newProfData);
    }

    try {
      await api.put(
        `/internacoes/${draggableId}/profissional-especialidade?especialidade=${especialidadeFilter}&profissional_id=${destination.droppableId}`
      );
      const destColumn = profColumns.find(c => c.id === destination.droppableId);
      toast.success(`Paciente atribuído a ${destColumn?.title || 'Sem Profissional'}`);
    } catch (error) {
      console.error('Error updating profissional:', error);
      toast.error('Erro ao atualizar profissional');
      fetchProfKanban();
    }
  };


  const executeMovePatient = async (fixar) => {
    if (!pendingMove) return;
    const { source, destination, draggableId } = pendingMove;
    setFixarDialogOpen(false);

    // Optimistic update
    const newEquipeData = { ...equipeData };
    const sourceItems = [...(newEquipeData[source.droppableId] || [])];
    const destItems = [...(newEquipeData[destination.droppableId] || [])];
    
    const itemIndex = sourceItems.findIndex(i => i.id === draggableId);
    if (itemIndex !== -1) {
      const [movedItem] = sourceItems.splice(itemIndex, 1);
      movedItem.equipe_id = destination.droppableId;
      if (fixar) movedItem.equipe_fixada = true;
      destItems.push(movedItem);
      
      newEquipeData[source.droppableId] = sourceItems;
      newEquipeData[destination.droppableId] = destItems;
      setEquipeData(newEquipeData);
    }

    try {
      await api.put(`/internacoes/${draggableId}/equipe?equipe_id=${destination.droppableId}&fixar=${fixar}`);
      const destColumn = equipeColumns.find(c => c.id === destination.droppableId);
      toast.success(`Paciente movido para ${destColumn?.title || destination.droppableId}${fixar ? ' (fixado)' : ''}`);
    } catch (error) {
      console.error('Error updating equipe:', error);
      toast.error('Erro ao atualizar equipe');
      fetchEquipeKanban();
    }
    setPendingMove(null);
  };

  const handleToggleLock = async (e, internacao) => {
    e.preventDefault();
    e.stopPropagation();
    const newFixar = !internacao.equipe_fixada;
    try {
      await api.put(`/internacoes/${internacao.id}/fixar-equipe?fixar=${newFixar}`);
      // Update local state
      const newEquipeData = { ...equipeData };
      for (const key in newEquipeData) {
        newEquipeData[key] = newEquipeData[key].map(item =>
          item.id === internacao.id ? { ...item, equipe_fixada: newFixar } : item
        );
      }
      setEquipeData(newEquipeData);
      toast.success(newFixar ? 'Paciente fixado nesta equipe' : 'Paciente desfixado');
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Erro ao fixar/desfixar');
    }
  };

  const handleToggleLockProfissional = async (e, internacao) => {
    e.preventDefault();
    e.stopPropagation();
    const newFixar = !internacao.profissional_fixado;
    try {
      await api.put(`/internacoes/${internacao.id}/fixar-profissional?especialidade=${especialidadeFilter}&fixar=${newFixar}`);
      // Update local state
      const newProfData = { ...profData };
      for (const key in newProfData) {
        newProfData[key] = newProfData[key].map(item =>
          item.id === internacao.id ? { ...item, profissional_fixado: newFixar } : item
        );
      }
      setProfData(newProfData);
      toast.success(newFixar ? 'Paciente fixado neste profissional' : 'Paciente desfixado');
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Erro ao fixar/desfixar');
    }
  };

  const handlePatientNameClick = (e, internacao) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedInternacao(internacao);
    setNovaTarefa({
      titulo: '',
      tipo: 'visita',
      data_prevista: todayLocal(),
      prioridade: 'media',
      status: 'a_fazer',
      observacoes: ''
    });
    setQuickTaskModalOpen(true);
  };

  const handleCreateQuickTask = async () => {
    if (!selectedInternacao || !novaTarefa.titulo) return;
    
    try {
      await api.post('/tarefas', {
        ...novaTarefa,
        internacao_id: selectedInternacao.id
      });
      toast.success(`Tarefa criada para ${selectedInternacao.paciente_nome}!`);
      setQuickTaskModalOpen(false);
      setSelectedInternacao(null);
      fetchKanbanData(); // Refresh to update task counts
    } catch (error) {
      toast.error('Erro ao criar tarefa');
      console.error('Error creating task:', error);
    }
  };

  const KanbanCard = ({ internacao, index }) => {
    const alertLevel = getDaysAlertLevel(internacao.dias_internacao);
    const taskPriority = internacao.maior_prioridade_tarefa;

    return (
      <Draggable 
        draggableId={internacao.id} 
        index={index}
        isDragDisabled={readOnly || (viewMode === 'equipe' ? false : viewMode !== 'medico')}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <Card
              className={`
                mb-3 cursor-pointer transition-all duration-200 hover:shadow-lg
                ${snapshot.isDragging ? 'shadow-xl rotate-2' : ''}
                ${alertLevel === 'critical' ? 'border-l-4 border-l-red-500' : ''}
                ${alertLevel === 'warning' ? 'border-l-4 border-l-yellow-500' : ''}
                ${taskPriority === 'alta' && internacao.tarefas_vencidas > 0 ? 'ring-2 ring-red-500 ring-opacity-50' : ''}
                ${internacao.equipe_fixada ? 'ring-1 ring-amber-400/60' : ''}
                ${internacao.profissional_fixado && especialidadeFilter ? 'ring-1 ring-red-400/60' : ''}
              `}
              data-testid={`kanban-card-${internacao.id}`}
            >
              <Link
                to={canOpenInternacao ? `/pacientes/${internacao.paciente_id}/internacao/${internacao.id}` : '#'}
                onClick={canOpenInternacao ? undefined : (e) => e.preventDefault()}
              >
                <CardContent className="p-3 space-y-2">
                  {/* Patient name, lock, and origem */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        {/* Lock icon for equipe mode */}
                        {!readOnly && internacao.equipe_fixada && viewMode === 'equipe' && !especialidadeFilter && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => handleToggleLock(e, internacao)}
                                  className="text-amber-500 hover:text-amber-600 transition-colors shrink-0"
                                  data-testid={`btn-lock-${internacao.id}`}
                                >
                                  <Lock className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Paciente fixado nesta equipe. Clique para desfixar.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {!readOnly && !internacao.equipe_fixada && viewMode === 'equipe' && !especialidadeFilter && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => handleToggleLock(e, internacao)}
                                  className="text-muted-foreground/30 hover:text-amber-500 transition-colors shrink-0"
                                  data-testid={`btn-unlock-${internacao.id}`}
                                >
                                  <Unlock className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Fixar paciente nesta equipe</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {/* Lock icon for specialty/professional mode */}
                        {!readOnly && especialidadeFilter && internacao.profissional_id && internacao.profissional_id !== 'sem_profissional' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => handleToggleLockProfissional(e, internacao)}
                                  className={`transition-colors shrink-0 ${
                                    internacao.profissional_fixado
                                      ? 'text-red-500 hover:text-red-600'
                                      : 'text-muted-foreground/30 hover:text-red-500'
                                  }`}
                                  data-testid={`btn-lock-prof-${internacao.id}`}
                                >
                                  {internacao.profissional_fixado ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{internacao.profissional_fixado ? 'Paciente fixado. Clique para desfixar.' : 'Fixar paciente neste profissional'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {!canCreateTask ? (
                          <span className="font-medium text-sm line-clamp-1 text-left" data-testid={`kanban-nome-${internacao.id}`}>
                            {internacao.paciente_nome}
                          </span>
                        ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={(e) => handlePatientNameClick(e, internacao)}
                                className="font-medium text-sm line-clamp-1 text-left hover:text-primary hover:underline underline-offset-2 transition-colors flex items-center gap-1"
                              >
                                {internacao.paciente_nome}
                                <Plus className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Clique para adicionar tarefa</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        )}
                      </div>
                      {/* CPF below name */}
                      {internacao.paciente_cpf && (
                        <p className="text-xs text-muted-foreground font-mono mt-0.5" data-testid={`cpf-${internacao.id}`}>
                          CPF: {displayCPF(internacao.paciente_cpf, canViewFullCPF)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {internacao.diagnostico_principal}
                      </p>
                      {internacao.medico_nome && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 line-clamp-1 mt-0.5" data-testid={`medico-${internacao.id}`}>
                          <Stethoscope className="h-3 w-3 inline mr-1" />
                          {internacao.medico_nome}
                        </p>
                      )}
                      {internacao.tecnico_nome && (
                        <p className="text-xs text-teal-700 dark:text-teal-400 line-clamp-1 mt-0.5" title={`Técnico(a) responsável: ${(internacao.tecnicos || []).join(', ')}`} data-testid={`tecnico-${internacao.id}`}>
                          <UserRound className="h-3 w-3 inline mr-1" />
                          {internacao.tecnico_nome}
                        </p>
                      )}
                    </div>
                    {(internacao.origem_grupo || internacao.origem) && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs shrink-0 flex items-center gap-1 ${internacao.origem_grupo ? '' : (ORIGEM_COLORS[internacao.origem] || '')}`}
                      style={internacao.origem_grupo ? {} : undefined}
                    >
                      {internacao.origem_display || internacao.origem_grupo || ORIGEM_LABELS[internacao.origem] || internacao.origem}
                    </Badge>
                  )}
                </div>

                {paResumo[internacao.id] && <div className="mb-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}><ResumoPA resumo={paResumo[internacao.id]} onClick={() => setPaSel(internacao.id)} testid={`pa-badge-${internacao.id}`} /></div>}
                {/* Date and days */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(internacao.data_internacao)}
                    {internacao.numero_prontuario && <span className="ml-1 font-mono text-[10px] text-muted-foreground" title="Nº prontuário" data-testid={`prontuario-${internacao.id}`}>#{internacao.numero_prontuario}</span>}
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`
                      ${alertLevel === 'critical' ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20' : ''}
                      ${alertLevel === 'warning' ? 'border-yellow-500 text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20' : ''}
                      ${alertLevel === 'normal' ? '' : ''}
                    `}
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    D+{internacao.dias_internacao}
                  </Badge>
                </div>

                {/* Task indicators */}
                {(internacao.tarefas_vencidas > 0 || internacao.tarefas_hoje > 0 || internacao.tarefas_pendentes_count > 0) && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border">
                    {internacao.tarefas_pendentes_count > 0 && (
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          taskPriority === 'alta' ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20' :
                          taskPriority === 'media' ? 'border-yellow-500 text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20' :
                          'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        }`}
                      >
                        {internacao.tarefas_pendentes_count} pendente(s)
                      </Badge>
                    )}
                    {internacao.tarefas_vencidas > 0 && (
                      <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                        <AlertTriangle className="h-3 w-3" />
                        {internacao.tarefas_vencidas} vencida(s)
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
              </Link>
            </Card>
          </div>
        )}
      </Draggable>
    );
  };

  const KanbanColumn = ({ column }) => {
    const rawItems = groupedData[column.id] || [];
    const items = kanbanSearch.trim() 
      ? rawItems.filter(item => {
          const searchLower = kanbanSearch.toLowerCase().trim();
          const searchDigits = kanbanSearch.replace(/\D/g, '');
          return (
            (item.paciente_nome || '').toLowerCase().includes(searchLower) ||
            (searchDigits.length >= 3 && (item.paciente_cpf || '').replace(/\D/g, '').includes(searchDigits))
          );
        })
      : rawItems;
    
    // Generate dynamic styles for equipe colors
    const headerStyle = column.cor ? {
      borderTopColor: column.cor,
      backgroundColor: `${column.cor}15` // 15 = ~9% opacity
    } : {};
    
    return (
      <div className="w-[320px] min-w-[320px] flex flex-col group" data-testid={`kanban-column-${column.id}`}>
        <div 
          className={`bg-card rounded-t-xl border-t-4 p-4 ${!column.cor ? `${column.color} ${column.bgLight}` : ''}`}
          style={headerStyle}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {column.cor && (
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: column.cor }}
                />
              )}
              <h2 className="font-semibold text-sm">{column.title}</h2>
              {/* Edit button for equipe mode (not in specialty mode) */}
              {canManageEquipes && viewMode === 'equipe' && !especialidadeFilter && column.id !== 'sem_equipe' && (
                <button
                  className="opacity-0 group-hover:opacity-100 hover:bg-muted rounded p-1 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEquipeModal(column);
                  }}
                  title="Editar equipe"
                >
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {column.capacidade > 0 && (
                <span className="text-xs text-muted-foreground">
                  {items.length}/{column.capacidade}
                </span>
              )}
              <Badge variant="secondary" className="font-mono">
                {items.length}
              </Badge>
            </div>
          </div>
          
          {/* Itinerary button for equipe mode OR professional mode */}
          {can('ver_mapa') && ((viewMode === 'equipe' && !especialidadeFilter && column.id !== 'sem_equipe') ||
            (especialidadeFilter && column.id !== 'sem_profissional')) && items.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 gap-1 text-xs h-7"
              onClick={() => especialidadeFilter
                ? fetchItinerarioProfissional(column.id)
                : fetchItinerario(column.id)
              }
              disabled={loadingItinerario}
            >
              {loadingItinerario ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Route className="h-3 w-3" />
              )}
              Sugerir Itinerário
            </Button>
          )}
        </div>
        
        <Droppable droppableId={column.id} isDropDisabled={viewMode !== 'equipe' && viewMode !== 'medico' && !especialidadeFilter}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`
                flex-1 bg-muted/30 rounded-b-xl p-3 min-h-[400px] 
                transition-colors duration-200
                ${snapshot.isDraggingOver ? 'bg-accent/10' : ''}
              `}
            >
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-32 w-full" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Nenhum paciente
                </div>
              ) : (
                items.map((item, index) => (
                  <KanbanCard key={item.id} internacao={item} index={index} />
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="kanban-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Kanban</h1>
            <p className="text-muted-foreground mt-1">
              {viewMode === 'equipe' && `Arraste os cartões para reorganizar equipes (${capacidade} pacientes/equipe)`}
              {viewMode === 'origem' && 'Visualização por origem do paciente'}
              {viewMode === 'los' && 'Visualização por tempo de internação'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* AI Distribution Button - Only in Equipe mode */}
            {canManageEquipes && viewMode === 'equipe' && (
              <>
                <Button 
                  onClick={handleDistribuirIA}
                  disabled={distribuindo}
                  className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  data-testid="btn-distribuir-ia"
                >
                  {distribuindo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Distribuir por Equipes (IA)
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => setManageEquipesModalOpen(true)}
                  className="gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Gerenciar Equipes
                </Button>
              </>
            )}
            
            {/* View Mode Selector */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-[180px]" data-testid="select-view-mode">
                  <SelectValue placeholder="Visualizar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipe">Equipe</SelectItem>
                  <SelectItem value="origem">Origem</SelectItem>
                  <SelectItem value="los">Tempo de Internação</SelectItem>
                  <SelectItem value="medico">Por médico</SelectItem>
                </SelectContent>
              </Select>
              {viewMode === 'origem' && (
                <Button 
                  variant={hgrExpanded ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setHgrExpanded(!hgrExpanded)}
                  className="gap-1"
                  data-testid="btn-toggle-hgr-subgrupos"
                >
                  {hgrExpanded ? 'Agrupar HGR' : 'Ver subgrupos HGR'}
                </Button>
              )}
            </div>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={viewMode === 'equipe' ? fetchEquipeKanban : fetchKanbanData}
              data-testid="btn-refresh-kanban"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {can('criar_pacientes') && (
            <Link to="/pacientes/novo">
              <Button className="gap-2" data-testid="btn-novo-paciente-kanban">
                <UserPlus className="h-4 w-4" />
                Novo Paciente
              </Button>
            </Link>
            )}
          </div>
        </div>

        {/* Search Bar + Specialty Filter */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar por nome ou CPF..."
              value={kanbanSearch}
              onChange={(e) => setKanbanSearch(e.target.value)}
              className="pl-10"
              data-testid="kanban-search-input"
            />
            {kanbanSearch && (
              <button
                onClick={() => setKanbanSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Specialty filter chips */}
          <div className="flex items-center gap-1.5" data-testid="kanban-especialidade-filter">
            <span className="text-xs text-muted-foreground mr-1">Especialidade:</span>
            <button
              onClick={() => setEspecialidadeFilter('')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                !especialidadeFilter ? 'bg-foreground text-background border-foreground' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="filter-esp-todos"
            >Todos</button>
            {[
              { key: 'fisio', label: 'Fisio', color: 'border-green-400 bg-green-50 text-green-700' },
              { key: 'fono', label: 'Fono', color: 'border-purple-400 bg-purple-50 text-purple-700' },
              { key: 'nutri', label: 'Nutri', color: 'border-orange-400 bg-orange-50 text-orange-700' },
              { key: 'psicologo', label: 'Psicólogo', color: 'border-blue-400 bg-blue-50 text-blue-700' },
              { key: 'clinico', label: 'Clínico', color: 'border-rose-400 bg-rose-50 text-rose-700' },
              { key: 'enfermeiro', label: 'Enfermeiro', color: 'border-teal-400 bg-teal-50 text-teal-700' },
            ].map(esp => (
              <button key={esp.key}
                onClick={() => setEspecialidadeFilter(especialidadeFilter === esp.key ? '' : esp.key)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                  especialidadeFilter === esp.key ? esp.color : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
                data-testid={`filter-esp-${esp.key}`}
              >{esp.label}</button>
            ))}
          </div>
        </div>

        {/* Kanban Board */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-6 overflow-x-auto pb-6 -mx-6 px-6">
            {currentColumns.map((column) => (
              <KanbanColumn key={column.id} column={column} />
            ))}
          </div>
        </DragDropContext>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-t pt-4">
          <span className="font-medium text-foreground">Tempo:</span>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500" />
            <span>30+ dias</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500" />
            <span>60+ dias</span>
          </div>
          
          <span className="font-medium text-foreground ml-4">Prioridade Tarefas:</span>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span>Alta</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse" />
            <span>Média</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
            <span>Baixa</span>
          </div>
        </div>

        {/* Quick Task Modal */}
        <Dialog open={quickTaskModalOpen} onOpenChange={setQuickTaskModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Tarefa</DialogTitle>
              <DialogDescription>
                Adicionar tarefa para <strong>{selectedInternacao?.paciente_nome}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  placeholder="Título da tarefa"
                  value={novaTarefa.titulo}
                  onChange={(e) => setNovaTarefa({...novaTarefa, titulo: e.target.value})}
                  data-testid="input-kanban-tarefa-titulo"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select 
                    value={novaTarefa.tipo}
                    onValueChange={(v) => setNovaTarefa({...novaTarefa, tipo: v})}
                  >
                    <SelectTrigger data-testid="select-kanban-tarefa-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select 
                    value={novaTarefa.prioridade}
                    onValueChange={(v) => setNovaTarefa({...novaTarefa, prioridade: v})}
                  >
                    <SelectTrigger data-testid="select-kanban-tarefa-prioridade">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data Prevista</Label>
                <Input
                  type="date"
                  value={novaTarefa.data_prevista}
                  onChange={(e) => setNovaTarefa({...novaTarefa, data_prevista: e.target.value})}
                  data-testid="input-kanban-tarefa-data"
                />
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  placeholder="Observações..."
                  value={novaTarefa.observacoes}
                  onChange={(e) => setNovaTarefa({...novaTarefa, observacoes: e.target.value})}
                  data-testid="input-kanban-tarefa-obs"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuickTaskModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateQuickTask} disabled={!novaTarefa.titulo} data-testid="btn-salvar-kanban-tarefa">
                <Plus className="h-4 w-4 mr-2" />
                Criar Tarefa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Equipes Modal */}
        <Dialog open={manageEquipesModalOpen} onOpenChange={setManageEquipesModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Gerenciar Equipes
              </DialogTitle>
              <DialogDescription>
                Edite nome, capacidade e cor das equipes. Clique em uma equipe para editar.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {equipeColumns.filter(c => c.id !== 'sem_equipe').map((equipe) => (
                <div 
                  key={equipe.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: equipe.cor || '#3B82F6' }}
                    />
                    <div>
                      <div className="font-medium">{equipe.title || equipe.nome}</div>
                      <div className="text-sm text-muted-foreground">
                        Capacidade: {equipe.capacidade || 7} pacientes
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {(groupedData[equipe.id] || []).length}/{equipe.capacidade || 7}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEquipeModal(equipe)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleDeleteEquipe(equipe.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleAddEquipe}
              >
                <Plus className="h-4 w-4" />
                Adicionar Nova Equipe
              </Button>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setManageEquipesModalOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Single Equipe Modal */}
        <Dialog open={equipeModalOpen} onOpenChange={setEquipeModalOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5" />
                Editar Equipe
              </DialogTitle>
              <DialogDescription>
                Altere configurações da equipe e ponto inicial da rota
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Nome da Equipe</Label>
                <Input
                  value={equipeForm.nome}
                  onChange={(e) => setEquipeForm({ ...equipeForm, nome: e.target.value })}
                  placeholder="Ex: Equipe Norte"
                  data-testid="equipe-nome-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Capacidade (pacientes)</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={equipeForm.capacidade}
                  onChange={(e) => setEquipeForm({ ...equipeForm, capacidade: parseInt(e.target.value) || 7 })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cor</Label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        equipeForm.cor === color.value ? 'border-foreground scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setEquipeForm({ ...equipeForm, cor: color.value })}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Ponto Inicial da Rota */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4" />
                  Ponto Inicial da Rota
                </h4>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Origem da rota</Label>
                    <Select
                      value={equipeForm.ponto_inicial_tipo}
                      onValueChange={(val) => setEquipeForm({ ...equipeForm, ponto_inicial_tipo: val })}
                    >
                      <SelectTrigger data-testid="ponto-inicial-tipo">
                        <SelectValue placeholder="Selecione a origem" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BASE">Base Lifeport</SelectItem>
                        <SelectItem value="TECNICO">Casa do Técnico</SelectItem>
                        <SelectItem value="PERSONALIZADO">Endereço Personalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {equipeForm.ponto_inicial_tipo === 'TECNICO' && (
                    <div className="space-y-2">
                      <Label>Técnico Responsável</Label>
                      <Select
                        value={equipeForm.ponto_inicial_tecnico_id}
                        onValueChange={(val) => setEquipeForm({ ...equipeForm, ponto_inicial_tecnico_id: val })}
                      >
                        <SelectTrigger data-testid="tecnico-select">
                          <SelectValue placeholder="Selecione o técnico" />
                        </SelectTrigger>
                        <SelectContent>
                          {usuarios
                            .filter(u => ['tecnico', 'enfermeiro', 'medico', 'operador'].includes(u.perfil?.toLowerCase()))
                            .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
                            .map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.nome} {u.endereco_texto ? `(${u.endereco_texto.slice(0, 30)}...)` : '(sem endereço)'}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        O endereço do técnico será usado como ponto de partida
                      </p>
                    </div>
                  )}

                  {equipeForm.ponto_inicial_tipo === 'PERSONALIZADO' && (
                    <>
                      <div className="space-y-2">
                        <Label>Endereço</Label>
                        <div className="flex gap-2">
                          <Input
                            className="flex-1"
                            value={equipeForm.ponto_inicial_endereco_texto}
                            onChange={(e) => setEquipeForm({ ...equipeForm, ponto_inicial_endereco_texto: e.target.value })}
                            placeholder="Digite o endereço completo"
                            data-testid="ponto-inicial-endereco"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setMapPickerOpen(true)}
                            className="gap-1"
                            data-testid="btn-selecionar-no-mapa"
                          >
                            <Map className="h-4 w-4" />
                            Mapa
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>Latitude</Label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={equipeForm.ponto_inicial_lat || ''}
                            onChange={(e) => setEquipeForm({ ...equipeForm, ponto_inicial_lat: parseFloat(e.target.value) || null })}
                            placeholder="Ex: 2.8120"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Longitude</Label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={equipeForm.ponto_inicial_lng || ''}
                            onChange={(e) => setEquipeForm({ ...equipeForm, ponto_inicial_lng: parseFloat(e.target.value) || null })}
                            placeholder="Ex: -60.7098"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Descrição (opcional)</Label>
                        <Input
                          value={equipeForm.ponto_inicial_descricao}
                          onChange={(e) => setEquipeForm({ ...equipeForm, ponto_inicial_descricao: e.target.value })}
                          placeholder="Ex: Casa do coordenador"
                        />
                      </div>
                      {equipeForm.ponto_inicial_lat && equipeForm.ponto_inicial_lng && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-2">
                          <p className="text-xs text-green-700 dark:text-green-300 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            Localização definida: {equipeForm.ponto_inicial_lat.toFixed(6)}, {equipeForm.ponto_inicial_lng.toFixed(6)}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {equipeForm.ponto_inicial_tipo === 'BASE' && (
                    <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                      A rota iniciará na Base Lifeport (Boa Vista, RR)
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEquipeModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEquipe} disabled={savingEquipe} data-testid="save-equipe-btn">
                {savingEquipe ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Map Picker Modal */}
        <MapPicker
          open={mapPickerOpen}
          onOpenChange={setMapPickerOpen}
          initialPosition={{
            lat: equipeForm.ponto_inicial_lat || 2.8120978,
            lng: equipeForm.ponto_inicial_lng || -60.7098542
          }}
          onSelect={(lat, lng, address) => {
            setEquipeForm({
              ...equipeForm,
              ponto_inicial_lat: lat,
              ponto_inicial_lng: lng,
              ponto_inicial_endereco_texto: address
            });
            toast.success('Localização selecionada!');
          }}
          title="Selecionar Ponto Inicial da Rota"
        />

        {/* Itinerary Modal */}
        <Dialog open={itinerarioModal} onOpenChange={setItinerarioModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Route className="h-5 w-5" />
                Itinerário - {itinerarioData?.equipe_nome}
              </DialogTitle>
              <DialogDescription>
                Rota otimizada para as visitas do dia
              </DialogDescription>
            </DialogHeader>
            
            {itinerarioData && (
              <div className="space-y-4 py-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold">{itinerarioData.total_paradas}</div>
                    <div className="text-xs text-muted-foreground">Visitas</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold">{itinerarioData.total_km} km</div>
                    <div className="text-xs text-muted-foreground">Distância Total</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold">~{itinerarioData.tempo_estimado_min} min</div>
                    <div className="text-xs text-muted-foreground">Tempo Estimado</div>
                  </div>
                </div>

                {/* Route List */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {itinerarioData.paradas?.map((parada, index) => (
                    <div 
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        parada.tipo === 'origem' || parada.tipo === 'retorno' 
                          ? 'bg-primary/5 border-primary/20' 
                          : 'bg-card'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            parada.tipo === 'origem' ? 'bg-green-500 text-white' :
                            parada.tipo === 'retorno' ? 'bg-red-500 text-white' :
                            'bg-primary text-primary-foreground'
                          }`}
                        >
                          {parada.tipo === 'origem' ? '🏠' : parada.tipo === 'retorno' ? '🏁' : parada.ordem}
                        </div>
                        {index < itinerarioData.paradas.length - 1 && (
                          <div className="w-0.5 h-8 bg-border mt-1" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">
                          {parada.tipo === 'origem' ? 'Partida: ' : parada.tipo === 'retorno' ? 'Retorno: ' : ''}
                          {parada.nome}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {parada.endereco || 'Endereço não disponível'}
                        </div>
                        {parada.tipo === 'visita' && (
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            📞 {parada.contato ? (
                              <span>{parada.contato_nome ? `${parada.contato_nome}: ` : ''}{parada.contato}</span>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </div>
                        )}
                        {parada.distancia_anterior_km && (
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                            <span className="flex items-center gap-1">
                              <Navigation className="h-3 w-3" />
                              {parada.distancia_anterior_km} km
                            </span>
                            <span className="flex items-center gap-1 text-primary font-medium">
                              <Clock className="h-3 w-3" />
                              ~{parada.tempo_anterior_min || Math.round(parada.distancia_anterior_km / 30 * 60)} min
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Patients without coordinates */}
                {itinerarioData.sem_coordenadas?.length > 0 && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50/50 dark:bg-amber-900/10 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4" />
                      {itinerarioData.sem_coordenadas.length} paciente(s) sem coordenadas GPS
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-500">
                      Esses pacientes não foram incluídos na rota otimizada. Atualize o endereço com CEP válido para incluí-los automaticamente.
                    </p>
                    <div className="space-y-1.5">
                      {itinerarioData.sem_coordenadas.map((p, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm bg-white/60 dark:bg-black/10 rounded p-2">
                          <div className="w-6 h-6 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-xs font-bold shrink-0">?</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{p.paciente_nome}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              <MapPin className="h-3 w-3 inline mr-1" />
                              {p.endereco || 'Endereço não cadastrado'}
                            </p>
                            {p.telefone && (
                              <p className="text-xs text-muted-foreground">
                                📞 {p.telefone}
                              </p>
                            )}
                          </div>
                          {p.endereco && (
                            <Button variant="ghost" size="sm" className="shrink-0 h-7 text-xs gap-1"
                              onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.endereco)}`, '_blank')}>
                              <Navigation className="h-3 w-3" /> Maps
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary with total patients info */}
                {itinerarioData.total_pacientes && itinerarioData.total_pacientes > itinerarioData.total_paradas && (
                  <p className="text-xs text-muted-foreground text-center">
                    Rota com {itinerarioData.total_paradas} de {itinerarioData.total_pacientes} pacientes 
                    ({itinerarioData.total_pacientes - itinerarioData.total_paradas} sem GPS)
                  </p>
                )}

                {/* Open in Google Maps */}
                {itinerarioData.paradas?.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {(() => {
                      const origemParada = itinerarioData.paradas.find(p => p.tipo === 'origem') || itinerarioData.paradas[0];
                      const retornoParada = itinerarioData.paradas.find(p => p.tipo === 'retorno') || itinerarioData.paradas[itinerarioData.paradas.length - 1];
                      const visitas = itinerarioData.paradas.filter(p => p.tipo === 'visita');
                      const origin = `${origemParada.lat},${origemParada.lng}`;
                      const destination = `${retornoParada.lat},${retornoParada.lng}`;
                      const MAX_WAYPOINTS = 23;

                      if (visitas.length <= MAX_WAYPOINTS) {
                        const waypoints = visitas.map(p => `${p.lat},${p.lng}`).join('|');
                        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ''}&travelmode=driving`;
                        return (
                          <Button variant="outline" className="w-full gap-2" data-testid="btn-abrir-google-maps"
                            onClick={() => window.open(url, '_blank')}>
                            <Navigation className="h-4 w-4" />
                            Abrir no Google Maps
                          </Button>
                        );
                      } else {
                        const chunks = [];
                        for (let i = 0; i < visitas.length; i += MAX_WAYPOINTS) {
                          chunks.push(visitas.slice(i, i + MAX_WAYPOINTS));
                        }
                        return chunks.map((chunk, idx) => {
                          const isFirst = idx === 0;
                          const isLast = idx === chunks.length - 1;
                          const chunkOrigin = isFirst ? origin : `${chunk[0].lat},${chunk[0].lng}`;
                          const chunkDest = isLast ? destination : `${chunk[chunk.length - 1].lat},${chunk[chunk.length - 1].lng}`;
                          const chunkWaypoints = (isFirst ? chunk : chunk.slice(1))
                            .filter((_, i) => !(isLast && i === chunk.length - (isFirst ? 1 : 2)))
                            .map(p => `${p.lat},${p.lng}`).join('|');
                          const url = `https://www.google.com/maps/dir/?api=1&origin=${chunkOrigin}&destination=${chunkDest}${chunkWaypoints ? `&waypoints=${chunkWaypoints}` : ''}&travelmode=driving`;
                          return (
                            <Button key={idx} variant="outline" className="w-full gap-2" data-testid={`btn-abrir-google-maps-part-${idx + 1}`}
                              onClick={() => window.open(url, '_blank')}>
                              <Navigation className="h-4 w-4" />
                              Abrir no Google Maps (parte {idx + 1}/{chunks.length})
                            </Button>
                          );
                        });
                      }
                    })()}
                    
                    {/* Export Options */}
                    <div className="flex gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="flex-1 gap-2">
                            <Share2 className="h-4 w-4" />
                            Exportar
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-48">
                          <DropdownMenuItem onClick={handleExportWhatsApp} className="gap-2 cursor-pointer">
                            <MessageCircle className="h-4 w-4 text-green-600" />
                            Enviar por WhatsApp
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleExportEmail} className="gap-2 cursor-pointer">
                            <Mail className="h-4 w-4 text-blue-600" />
                            Enviar por E-mail
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleDownloadTxt} className="gap-2 cursor-pointer">
                            <Download className="h-4 w-4" />
                            Baixar arquivo (.txt)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      <Button variant="outline" onClick={handlePrint} className="gap-2">
                        <Printer className="h-4 w-4" />
                        Imprimir
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setItinerarioModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lock/Fix Patient Dialog */}
        <Dialog open={fixarDialogOpen} onOpenChange={(open) => { if (!open) { setFixarDialogOpen(false); setPendingMove(null); } }}>
          <DialogContent className="sm:max-w-[420px]" data-testid="dialog-fixar-paciente">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-amber-500" />
                {pendingMove?.isLocked 
                  ? 'Este paciente está fixado'
                  : 'Deseja fixar este paciente nesta equipe?'
                }
              </DialogTitle>
              <DialogDescription>
                {pendingMove?.isLocked ? (
                  <>
                    <span className="font-medium">{pendingMove?.pacienteNome}</span>
                    {' '}está fixado na equipe atual. Deseja mover para{' '}
                    <span className="font-medium">
                      {equipeColumns.find(c => c.id === pendingMove?.destination?.droppableId)?.title || 'nova equipe'}
                    </span>?
                  </>
                ) : (
                  <>
                    {pendingMove?.pacienteNome && (
                      <span className="font-medium">{pendingMove.pacienteNome}</span>
                    )}
                    {' '}será movido para{' '}
                    <span className="font-medium">
                      {equipeColumns.find(c => c.id === pendingMove?.destination?.droppableId)?.title || 'nova equipe'}
                    </span>.
                    {' '}Pacientes fixados não são redistribuídos pela IA.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => { setFixarDialogOpen(false); setPendingMove(null); }}
                data-testid="btn-fixar-cancelar"
              >
                Cancelar
              </Button>
              {pendingMove?.isLocked ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => executeMovePatient(false)}
                    data-testid="btn-mover-sem-fixar"
                  >
                    <Unlock className="h-4 w-4 mr-2" />
                    Mover sem fixar
                  </Button>
                  <Button
                    className="gap-2 bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => executeMovePatient(true)}
                    data-testid="btn-mover-e-fixar"
                  >
                    <Lock className="h-4 w-4" />
                    Mover e fixar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => executeMovePatient(false)}
                    data-testid="btn-fixar-nao"
                  >
                    Não fixar
                  </Button>
                  <Button
                    className="gap-2 bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => executeMovePatient(true)}
                    data-testid="btn-fixar-sim"
                  >
                    <Lock className="h-4 w-4" />
                    Fixar
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <PrimeirosAtendimentosDialog internacaoId={paSel} open={!!paSel} onOpenChange={(o) => { if (!o) setPaSel(null); }} onChanged={loadPaResumo} />
    </Layout>
  );
};

export default Kanban;
